import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import './AdminOverview.css';

const AdminOverview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeEnrollments: 0,
    totalBookings: 0,
    totalOrders: 0,
    newUsersToday: 0,
    newUsersThisWeek: 0,
    recentUsers: [],
    userSignupTrend: []
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiService.getAdminStats();
        if (response.success) {
          setStats(response.data);
        }
      } catch (error) {
        console.error("Failed to load admin stats:", error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStats();
  }, []);

  const adminModules = [
    { title: 'Pooja Bookings', description: 'View and manage all online pooja reservations.', icon: 'fas fa-gopuram', link: '/admin/bookings', color: '#e53935' },
    { title: 'Store Orders', description: 'Process and track physical item purchases.', icon: 'fas fa-box', link: '/admin/orders', color: '#f57c00' },
    { title: 'Store Products', description: 'Add, edit, or remove items from the Bhakti Store.', icon: 'fas fa-store', link: '/admin/products', color: '#43a047' },
    { title: 'Course Catalogues', description: 'Manage main categories for Yoga and Meditation.', icon: 'fas fa-folder-open', link: '/admin/catalogues', color: '#1e88e5' },
    { title: 'Yoga Courses', description: 'Create and edit course content, modules, and lessons.', icon: 'fas fa-book-open', link: '/admin/courses', color: '#8e24aa' },
    { title: 'Student Enrollments', description: 'Manage user access, subscriptions, and progress.', icon: 'fas fa-user-graduate', link: '/admin/enrollments', color: '#00acc1' },
    { title: 'Daily Notice', description: 'Manage daily notifications/highlights.', icon: 'fas fa-bell', link: '/admin/daily-spiritual-info', color: '#f0dc00' },
    { title: 'Bhakti Darshan', description: 'Manage live shrine streams, thumbnails, and visibility.', icon: 'fas fa-video', link: '/admin/bhakti-darshan', color: '#db2777' },
    { title: 'Bal Chitra Katha', description: 'Add or delete curated kids YouTube spiritual channels.', icon: 'fas fa-child', link: '/admin/bhakti-bal-chitra-katha', color: '#6366f1' },
    { title: 'Vastu Consultations', description: 'Monitor Vastu bookings, assignments, meetings, and revenue.', icon: 'fas fa-drafting-compass', link: '/admin/vastu-consultations', color: '#16715c' },
    { title: 'Vastu Consultants', description: 'Add and manage third-party Vastu service providers.', icon: 'fas fa-user-tie', link: '/admin/vastu-consultants', color: '#b75c1b' }
  ];

  const maxSignupCount = Math.max(...(stats.userSignupTrend || []).map((day) => day.count), 1);
  const formatJoinedDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    return new Date(dateValue).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-welcome-section">
        <div className="welcome-text">
          <h1>Welcome back, {user?.fullName?.split(' ')[0] || 'Admin'}!</h1>
          <p>Here is your command center. Select a module below to start managing Anandmaya.</p>
        </div>
        <div className="system-status">
          <span className="status-dot pulsing"></span>
          System Online
        </div>
      </div>

      {/* --- NEW STATS ROW --- */}
      <div className="admin-stats-row">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e3f2fd', color: '#1e88e5' }}>
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-details">
            <h3>Total Users</h3>
            <p>{isLoadingStats ? '...' : stats.totalUsers}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#e0f7fa', color: '#00acc1' }}>
            <i className="fas fa-user-graduate"></i>
          </div>
          <div className="stat-details">
            <h3>Active Enrollments</h3>
            <p>{isLoadingStats ? '...' : stats.activeEnrollments}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#ffebee', color: '#e53935' }}>
            <i className="fas fa-om"></i>
          </div>
          <div className="stat-details">
            <h3>Total Poojas</h3>
            <p>{isLoadingStats ? '...' : stats.totalBookings}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff3e0', color: '#f57c00' }}>
            <i className="fas fa-shopping-bag"></i>
          </div>
          <div className="stat-details">
            <h3>Store Orders</h3>
            <p>{isLoadingStats ? '...' : stats.totalOrders}</p>
          </div>
        </div>
      </div>
      {/* ------------------- */}

      <div className="user-analytics-section">
        <div className="analytics-header">
          <div>
            <h2>User Growth</h2>
            <p>Recent signup activity across Anandmaya accounts.</p>
          </div>
          <div className="growth-summary">
            <div>
              <span>Today</span>
              <strong>{isLoadingStats ? '...' : stats.newUsersToday}</strong>
            </div>
            <div>
              <span>Last 7 Days</span>
              <strong>{isLoadingStats ? '...' : stats.newUsersThisWeek}</strong>
            </div>
          </div>
        </div>

        <div className="analytics-grid">
          <div className="signup-trend-panel">
            <h3>7-Day Signup Trend</h3>
            <div className="signup-trend-bars">
              {(stats.userSignupTrend || []).map((day) => (
                <div className="trend-day" key={day.date}>
                  <div className="trend-bar-track">
                    <div
                      className="trend-bar-fill"
                      style={{ height: `${Math.max((day.count / maxSignupCount) * 100, day.count > 0 ? 12 : 0)}%` }}
                    />
                  </div>
                  <strong>{day.count}</strong>
                  <span>{day.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="recent-users-panel">
            <h3>Recent New Users</h3>
            <div className="recent-users-table">
              {(stats.recentUsers || []).length > 0 ? (
                stats.recentUsers.map((recentUser) => (
                  <div className="recent-user-row" key={recentUser._id}>
                    <div>
                      <strong>{recentUser.fullName || 'Unnamed User'}</strong>
                      <span>{recentUser.email}</span>
                    </div>
                    <div className="recent-user-meta">
                      <span>{recentUser.role}</span>
                      <small>{formatJoinedDate(recentUser.createdAt)}</small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-analytics-state">
                  {isLoadingStats ? 'Loading recent users...' : 'No recent users found.'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="admin-modules-grid">
        {adminModules.map((module, index) => (
          <Link to={module.link} key={index} className="admin-module-card">
            <div className="module-icon-wrapper" style={{ backgroundColor: `${module.color}15`, color: module.color }}>
              <i className={module.icon}></i>
            </div>
            <div className="module-info">
              <h3>{module.title}</h3>
              <p>{module.description}</p>
            </div>
            <div className="module-arrow">
              <i className="fas fa-chevron-right"></i>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default AdminOverview;
